import { useState, useEffect, useRef, type CSSProperties } from "react";
import { starterTemplates, type StarterTemplate } from "../../lib/table-starter-templates";
import { useCreateTable } from "../../hooks/use-create-table";
import type { EntityTypeDefinition } from "../../../lib/types";

interface NewTableModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownerAgentId: string;
  onCreated?: (tableNodeId: string) => void;
}

function derivePlural(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "";
  if (/[^aeiou]y$/i.test(trimmed)) return trimmed.slice(0, -1) + "ies";
  if (/(s|x|z|ch|sh)$/i.test(trimmed)) return trimmed + "es";
  return trimmed + "s";
}

function derivePrefix(name: string): string {
  const clean = name.trim().replace(/[^a-zA-Z]/g, "").toUpperCase();
  if (clean.length <= 3) return clean || "TBL";
  const consonants = clean.replace(/[AEIOU]/g, "");
  if (consonants.length >= 3) return consonants.slice(0, 3);
  return clean.slice(0, 3);
}

export function NewTableModal({ open, onOpenChange, ownerAgentId, onCreated }: NewTableModalProps) {
  const [name, setName] = useState("");
  const [plural, setPlural] = useState("");
  const [prefix, setPrefix] = useState("");
  const [description, setDescription] = useState("");
  const [templateId, setTemplateId] = useState("empty");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const createTable = useCreateTable();

  useEffect(() => {
    if (open) {
      setName("");
      setPlural("");
      setPrefix("");
      setDescription("");
      setTemplateId("empty");
      setShowAdvanced(false);
      setError("");
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const selectedTemplate = (starterTemplates.find((t) => t.id === templateId) ?? starterTemplates[0])!;
  const effectivePlural = plural || derivePlural(name);
  const effectivePrefix = prefix || derivePrefix(name);
  const slug = name.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName || trimmedName.length > 60) return;
    if (description.length > 200) return;

    setError("");

    const definition: Omit<EntityTypeDefinition, "_version"> = {
      slug,
      name: trimmedName,
      plural: effectivePlural,
      id_prefix: effectivePrefix,
      data_class: "master",
      fields: selectedTemplate.fields,
      ...(description.trim() ? { instructions: description.trim() } : {}),
      ...(selectedTemplate.workflow ? { workflow: selectedTemplate.workflow as unknown as EntityTypeDefinition["workflow"] } : {}),
    };

    createTable.mutate(
      { definition, parentAgentId: ownerAgentId },
      {
        onSuccess: (result) => {
          if (result.tableNodeId) {
            onCreated?.(result.tableNodeId);
          }
          onOpenChange(false);
        },
        onError: (err: Error) => {
          const msg = err.message;
          if (msg.includes("name_exists") || msg.includes("already exists") || msg.includes("slug_exists")) {
            setError("A table with this name already exists. Pick a different name.");
          } else if (msg.includes("plan_limit")) {
            setError("Your workspace plan has reached its table limit. Upgrade in Settings.");
          } else {
            setError(msg || "Failed to create table.");
          }
        },
      },
    );
  }

  function handleClose() {
    if (!createTable.isPending) {
      onOpenChange(false);
    }
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    fontFamily: "var(--font-sans)",
  };

  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 420,
    maxWidth: "90vw",
  };

  const fieldStyle: CSSProperties = {
    marginBottom: 16,
  };

  const labelStyle: CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    marginBottom: 4,
  };

  const inputStyle: CSSProperties = {
    width: "100%",
    fontSize: 13,
    padding: "8px 10px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
    boxSizing: "border-box",
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div
        style={modal}
        role="dialog"
        aria-label="New table"
        onKeyDown={(e) => { if (e.key === "Escape") handleClose(); }}
      >
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>
            New table
          </div>
          <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>
            Lives at workspace root for shared access; this agent owns it. Tables are
            workspace-scoped data — ownership is metadata, not location.
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Name</label>
            <input
              ref={nameRef}
              type="text"
              style={inputStyle}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Tasks"
              maxLength={60}
            />
            {error && (
              <div style={{ fontSize: 12, color: "var(--c-red-600)", marginTop: 4 }}>
                {error}
              </div>
            )}
          </div>

          <div style={fieldStyle}>
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                fontSize: 12,
                color: "var(--color-text-tertiary)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontFamily: "var(--font-sans)",
              }}
            >
              <i
                className={`ti ti-chevron-${showAdvanced ? "down" : "right"}`}
                style={{ fontSize: 12 }}
                aria-hidden="true"
              />
              Advanced
            </button>
            {showAdvanced && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Plural</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={plural}
                    onChange={(e) => setPlural(e.target.value)}
                    placeholder={derivePlural(name) || "e.g. Tasks"}
                  />
                </div>
                <div>
                  <label style={labelStyle}>ID prefix</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={prefix}
                    onChange={(e) => setPrefix(e.target.value)}
                    placeholder={derivePrefix(name) || "e.g. TSK"}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <input
                    type="text"
                    style={inputStyle}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional description"
                    maxLength={200}
                  />
                </div>
              </div>
            )}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Starter template</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {starterTemplates.map((t) => (
                <TemplateOption
                  key={t.id}
                  template={t}
                  selected={templateId === t.id}
                  onSelect={() => setTemplateId(t.id)}
                />
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
            <button
              type="button"
              onClick={handleClose}
              style={{ fontSize: 13, padding: "8px 14px" }}
              disabled={createTable.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-variant="primary"
              disabled={!name.trim() || createTable.isPending}
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              {createTable.isPending ? "Creating..." : "Create table"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TemplateOption({
  template,
  selected,
  onSelect,
}: {
  template: StarterTemplate;
  selected: boolean;
  onSelect: () => void;
}) {
  const style: CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "8px 10px",
    borderRadius: "var(--border-radius-sm)",
    border: selected ? "1px solid var(--color-text-tertiary)" : "var(--kuvio-border-default)",
    background: selected ? "var(--color-background-secondary)" : "var(--color-background-primary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  };

  return (
    <label style={style}>
      <input
        type="radio"
        name="starter-template"
        value={template.id}
        checked={selected}
        onChange={onSelect}
        style={{ marginTop: 2, flexShrink: 0 }}
      />
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>
          {template.label}
        </div>
        <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 1 }}>
          {template.description}
        </div>
      </div>
    </label>
  );
}
